/**
 * tokens.ts — numeric/string equivalents of CSS design tokens.
 * Keep in sync with :root in globals.css.
 */
import type { CSSProperties } from 'react';

export const space = {
  1: 4, 2: 8, 3: 12, 4: 16, 5: 24, 6: 32, 7: 40, 8: 48,
} as const;

export const radius = {
  sm: 4, md: 8, lg: 12, xl: 16, '2xl': 24, '3xl': 32, '4xl': 40, full: 9999,
} as const;

export const text = {
  xs: '0.7rem', sm: '0.8rem', base: '0.875rem', md: '1rem', lg: '1.125rem', xl: '1.25rem', '2xl': '1.5rem', '3xl': '2.25rem', '4xl': '3rem',
} as const;

export const icon = {
  xs: 12, sm: 16, md: 20, lg: 32, xl: 48, display: 128,
} as const;

export const controlSize = {
  sm: 20, md: 36, lg: 48,
} as const;

/** Material Symbols optical weight (`wght` axis) */
export const iconWeight = {
  light: 300, regular: 400, bold: 700,
} as const;

export const opacity = {
  dim: 0.3, muted: 0.4, half: 0.5, soft: 0.6,
} as const;

export const weight = {
  normal: 400, medium: 500, semibold: 600,
} as const;

export const duration = {
  fast: 100, normal: 150, slow: 200, emphasis: 300, atmospheric: 500,
} as const;

export const leading = {
  tight: 1.3, normal: 1.6, relaxed: 1.7,
} as const;

export const tracking = {
  body: '-0.01em', caps: '0.05em', widest: '0.2em', heading: '-0.03em',
} as const;

/**
 * Shared serif-heading style block.
 *
 * Spread this into a <Text> or native element's `style` prop for any section
 * heading that needs to match the prototype's tight-tracked, upright serif
 * aesthetic (greeting, "Today", empty-state titles, etc). Overrides Text's
 * default inline letterSpacing/lineHeight — that's why it lives as a style
 * object rather than a CSS class.
 *
 * Consumers still set `fontSize` and `color` per usage.
 */
export const serifHeading: CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontWeight: 500,
  letterSpacing: 'var(--tracking-heading)',
  lineHeight: 'var(--leading-display)',
};

/**
 * Italic serif heading for hero/display moments (Login hero, library subtitles,
 * calendar empty states). Nearly-neutral tracking suits the italic optical form.
 *
 * Consumers still set `fontSize` and `color` per usage.
 */
export const serifHeadingItalic: CSSProperties = {
  fontFamily: 'var(--font-serif)',
  fontWeight: 500,
  fontStyle: 'italic',
  letterSpacing: 'var(--tracking-display-serif)',
  lineHeight: 'var(--leading-display)',
};

/**
 * Quiet Focus Ring — the canonical keyboard-focus ring defined in the Nula
 * design system (§22). Spread into `style` when applying focus styles
 * imperatively. For conditional usage: `style={focused ? focusRing : {}}`.
 */
export const focusRing: CSSProperties = {
  boxShadow: '0 0 0 2px var(--surface), 0 0 0 3.5px var(--accent)',
};

/**
 * Uppercase meta-label style for section headers, card type labels, and
 * similar small descriptive caps text (e.g., "RECENTLY EDITED", "DOCUMENT").
 * Consumers add `color` and `fontSize` per usage.
 */
export const metaLabel: CSSProperties = {
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  fontWeight: 600,
};

/**
 * Uppercase tab-label style for tab bars and segmented controls.
 * Uses medium weight and caps tracking — lighter than metaLabel.
 * Consumers add `color` and conditional `fontWeight` per usage.
 */
export const tabLabel: CSSProperties = {
  letterSpacing: '0.05em',
  textTransform: 'uppercase',
  fontWeight: 500,
};

/**
 * Small uppercase caps label used as a section header or inline metadata
 * (e.g., "UPCOMING", "NO EVENTS", "SEARCH RESULTS"). Has an explicit fontSize
 * unlike metaLabel. Consumers typically override `color` and sometimes
 * `opacity`, `paddingLeft`, or `paddingBottom` per use site.
 */
export const sectionCapsLabel: CSSProperties = {
  fontSize: 'var(--text-xs)',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.2em',
  color: 'var(--on-surface-variant)',
};

/**
 * Frosted-glass pill skeleton — the structural part of a floating pill-shaped
 * panel (floating toolbar, scroll-to-top button, floating action bar). Sets
 * the webkit-prefixed backdrop-filter pair, full-radius, subtle border, and
 * a default soft shadow. Callers typically override `backdropFilter` +
 * `WebkitBackdropFilter` together with a different blur value, and sometimes
 * `boxShadow` and `background`.
 *
 * Example:
 *   style={{
 *     ...glassPill,
 *     background: 'color-mix(in srgb, var(--surface) 85%, transparent)',
 *     boxShadow: 'var(--shadow-hover)',
 *   }}
 */
export const glassPill: CSSProperties = {
  backdropFilter: 'blur(12px)',
  WebkitBackdropFilter: 'blur(12px)',
  borderRadius: 'var(--radius-full)',
  boxShadow: 'var(--shadow-soft)',
  border: '1px solid color-mix(in srgb, var(--on-surface) 5%, transparent)',
};

export const ease = {
  standard: [0.4, 0, 0.2, 1] as const,
  decelerate: [0, 0, 0.2, 1] as const,
  accelerate: [0.4, 0, 1, 1] as const,
} as const;

export const zIndex = {
  content: 1,   // local stacking within a component
  sticky: 10,   // sticky headers, actionbars
  sidebar: 20,  // sidebar overlays
  dropdown: 30, // dropdowns, selects, autocomplete, mentions
  fixed: 40,    // FAB, floating toolbars, find bar, zen bar
  overlay: 50,  // backdrop behind modals
  modal: 60,    // modals, drawers, assistant panel
  popover: 70,  // popovers, tooltips — must be above modal
  toast: 80,    // notifications
  max: 90,      // topmost — emoji picker, link preview cards
} as const;

export const header = {
  page: {
    fontFamily: 'var(--font-serif)',
    fontSize: 'var(--text-3xl)',
    fontWeight: 'var(--weight-medium)',
    fontStyle: 'italic' as const,
    lineHeight: 'var(--leading-tight)',
  },
} as const;

export const layout = {
  contentWidthSm: 680,
  contentWidth: 896,
  contentWidthLg: 1152,
  searchWidth: 600,
  drawerWidth: 320,
  sidebarWidth: 256,
  sidebarWidthCollapsed: 80,
} as const;

export const breakpoint = {
  sm: 640, md: 768, lg: 1024, xl: 1280,
} as const;

/** The 14-color "Quiet Pastel" system — CSS variable references */
export const pastel = [
  'var(--pastel-mist-blue)',
  'var(--pastel-terracotta)',
  'var(--pastel-lavender)',
  'var(--pastel-sage)',
  'var(--pastel-rose)',
  'var(--pastel-petal)',
  'var(--pastel-barley)',
  'var(--pastel-grey)',
  'var(--pastel-teal)',
  'var(--pastel-sand)',
  'var(--pastel-ink)',
  'var(--pastel-linen)',
  'var(--pastel-dew)',
  'var(--pastel-arctic)',
] as const;

/** Deterministic pastel color from any string (tag name, ID, etc.) */
export function pastelFromString(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return pastel[Math.abs(hash) % pastel.length];
}

/** Helper: returns `var(--space-N)` */
export const spaceVar = (n: keyof typeof space) => `var(--space-${n})`;
/** Helper: returns `var(--radius-X)` */
export const radiusVar = (n: keyof typeof radius) => `var(--radius-${n})`;
/** Helper: returns `var(--text-X)` */
export const textVar = (n: keyof typeof text) => `var(--text-${n})`;
